"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Database, Server, Clock, ShieldCheck, FileText, ExternalLink, Lock, Mail } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableCaption } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const retentionRecords = [{
  category: "Newsletter subscribers",
  dataTypes: "Email address, subscription preferences, opt-in timestamp",
  retentionPeriod: "Duration of active subscription + 30 days after unsubscribe",
  purpose: "Delivering editorial briefings and preserving audit-compliant consent logs",
  disposalMethod: "Automated cryptographic purge from production databases"
}, {
  category: "Research report downloads",
  dataTypes: "Name, business email, organization, job seniority, download history",
  retentionPeriod: "24 months from last verified user interaction",
  purpose: "Access validation, readership analytics, and editorial trend attribution",
  disposalMethod: "Anonymized aggregation; personal identifiers permanently deleted"
}, {
  category: "Peer review & editorial inquiries",
  dataTypes: "Author correspondence, research submissions, conflict disclosures",
  retentionPeriod: "5 years following publication or final editorial review",
  purpose: "Journalistic provenance, integrity verification, and legal defense",
  disposalMethod: "Cold archive retention with restricted compliance-only access"
}, {
  category: "Server logs & access metrics",
  dataTypes: "Pseudonymized IP addresses, browser headers, referring URLs",
  retentionPeriod: "90 days rolling retention window",
  purpose: "Network security, DDOS mitigation, and infrastructure diagnostics",
  disposalMethod: "Automated FIFO log overwrite and rotational zeroization"
}];
const thirdPartyProcessors = [{
  name: "Amazon Web Services (AWS)",
  category: "Cloud Infrastructure & Storage",
  location: "United States (US-East / US-West)",
  purpose: "Encrypted primary database hosting and secure static asset delivery",
  safeguards: "SOC 2 Type II, ISO 27001, Standard Contractual Clauses (SCCs)"
}, {
  name: "Postmark / ActiveCampaign",
  category: "Editorial Dispatch & Email",
  location: "United States & European Union",
  purpose: "Transactional dispatch for reports, issue alerts, and subscription management",
  safeguards: "GDPR DPA in place, TLS 1.3 in transit, AES-256 at rest"
}, {
  name: "Plausible Analytics",
  category: "Privacy-First Audience Analytics",
  location: "European Union (Estonia / Germany)",
  purpose: "Aggregate readership telemetry without personal identifiers or cross-site tracking",
  safeguards: "Cookie-less tracking, zero PII retention, 100% GDPR compliant"
}, {
  name: "Stripe",
  category: "Payment Processing & Billing",
  location: "United States & Global",
  purpose: "Secure billing processing for institutional subscriptions and premium reports",
  safeguards: "PCI-DSS Level 1 certified, tokenized transactions, end-to-end encryption"
}];
export default function Retention() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 14
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="data-retention-third-party-processors" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }} variants={containerVariants} className="max-w-[840px] mb-12 sm:mb-16">
          <motion.div variants={itemVariants} className="flex items-center gap-2 mb-3">
            <span className="inline-block w-2 h-2 rounded-full bg-accent" />
            <span className="text-xs font-semibold tracking-wider uppercase font-sans text-muted-foreground">
              Institutional Governance
            </span>
          </motion.div>

          <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-[2.5rem] font-semibold tracking-tight font-serif text-foreground leading-[1.2] mb-4">
            Data retention schedules and vetted processors
          </motion.h2>

          {/* Masthead Rule Motif */}
          <motion.div variants={itemVariants} className="w-full my-4">
            <div className="h-[1px] bg-foreground w-full" />
            <div className="h-[0.5px] bg-border w-full mt-[2px]" />
          </motion.div>

          <motion.p variants={itemVariants} className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            MinfoMedia operates under strict data minimization standards. We retain subscriber, reader, and research records strictly for the periods specified below, and partner solely with infrastructure providers vetted for security, regulatory compliance, and verified contractual safeguards.
          </motion.p>
        </motion.div>

        {/* Section 1: Retention Schedules */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-60px"
      }} variants={containerVariants} className="mb-16 lg:mb-20">
          <motion.div variants={itemVariants} className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-muted text-foreground">
                <Clock className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-semibold font-serif text-foreground">
                  Data retention schedules
                </h3>
                <p className="text-sm text-muted-foreground font-sans">
                  Systematic preservation limits applied to collected information categories.
                </p>
              </div>
            </div>

            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-sm border border-border bg-card text-xs font-mono text-muted-foreground">
              <Database className="w-3.5 h-3.5 text-accent" />
              <span>Audit standard: ISO/IEC 27001</span>
            </div>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card className="rounded-md border border-border bg-card shadow-xs overflow-hidden">
              <Table>
                <TableHeader className="bg-muted/40">
                  <TableRow className="border-border">
                    <TableHead className="font-semibold text-foreground py-3.5 pl-4 sm:pl-6">Data Category</TableHead>
                    <TableHead className="font-semibold text-foreground py-3.5">Collected Attributes</TableHead>
                    <TableHead className="font-semibold text-foreground py-3.5">Retention Horizon</TableHead>
                    <TableHead className="font-semibold text-foreground py-3.5">Editorial & Legal Purpose</TableHead>
                    <TableHead className="font-semibold text-foreground py-3.5 pr-4 sm:pr-6">Disposal Routine</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {retentionRecords.map((record, i) => <TableRow key={record.category} data-index={i} className="border-border/70 hover:bg-muted/30">
                      <TableCell className="font-medium text-foreground py-4 pl-4 sm:pl-6 align-top whitespace-normal min-w-[180px]">
                        <div className="font-serif font-semibold text-base mb-1">{record.category}</div>
                        <span className="inline-flex items-center gap-1 text-[11px] font-mono text-muted-foreground">
                          <Lock className="w-3 h-3 text-accent" /> Encrypted storage
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground py-4 align-top whitespace-normal min-w-[200px] text-sm font-sans">
                        {record.dataTypes}
                      </TableCell>
                      <TableCell className="py-4 align-top whitespace-normal min-w-[170px]">
                        <span className="inline-block px-2.5 py-1 rounded-sm bg-muted text-foreground text-xs font-mono font-medium">
                          {record.retentionPeriod}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground py-4 align-top whitespace-normal min-w-[220px] text-sm font-sans">
                        {record.purpose}
                      </TableCell>
                      <TableCell className="text-foreground py-4 pr-4 sm:pr-6 align-top whitespace-normal min-w-[200px] text-xs font-mono text-muted-foreground">
                        {record.disposalMethod}
                      </TableCell>
                    </TableRow>)}
                </TableBody>
                <TableCaption className="text-xs text-muted-foreground font-sans px-4 pb-2">
                  Retention periods are reviewed bi-annually by MinfoMedia Legal & Data Governance counsel.
                </TableCaption>
              </Table>
            </Card>
          </motion.div>
        </motion.div>

        {/* Section 2: Vetted Third-Party Processors */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-60px"
      }} variants={containerVariants} className="mb-16 lg:mb-20">
          <motion.div variants={itemVariants} className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-muted text-foreground">
                <Server className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-semibold font-serif text-foreground">
                  Vetted sub-processors and vendors
                </h3>
                <p className="text-sm text-muted-foreground font-sans">
                  Authorized service providers bound by strict Data Processing Agreements (DPAs).
                </p>
              </div>
            </div>

            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-sm border border-border bg-card text-xs font-mono text-muted-foreground">
              <ShieldCheck className="w-3.5 h-3.5 text-accent" />
              <span>EU-US DPF & SCCs Active</span>
            </div>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card className="rounded-md border border-border bg-card shadow-xs overflow-hidden">
              <Table>
                <TableHeader className="bg-muted/40">
                  <TableRow className="border-border">
                    <TableHead className="font-semibold text-foreground py-3.5 pl-4 sm:pl-6">Processor Entity</TableHead>
                    <TableHead className="font-semibold text-foreground py-3.5">Processing Domain</TableHead>
                    <TableHead className="font-semibold text-foreground py-3.5">Primary Processing Jurisdiction</TableHead>
                    <TableHead className="font-semibold text-foreground py-3.5">Operational Scope</TableHead>
                    <TableHead className="font-semibold text-foreground py-3.5 pr-4 sm:pr-6">Security & Transfer Mechanism</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {thirdPartyProcessors.map((processor, i) => <TableRow key={processor.name} data-index={i} className="border-border/70 hover:bg-muted/30">
                      <TableCell className="font-medium text-foreground py-4 pl-4 sm:pl-6 align-top whitespace-normal min-w-[180px]">
                        <div className="font-serif font-semibold text-base mb-1">{processor.name}</div>
                        <span className="inline-block text-[11px] font-mono text-accent bg-secondary/80 px-1.5 py-0.5 rounded-sm">
                          Verified DPA
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground py-4 align-top whitespace-normal min-w-[180px] text-sm font-sans">
                        {processor.category}
                      </TableCell>
                      <TableCell className="text-foreground py-4 align-top whitespace-normal min-w-[170px] text-sm font-sans font-medium">
                        {processor.location}
                      </TableCell>
                      <TableCell className="text-muted-foreground py-4 align-top whitespace-normal min-w-[220px] text-sm font-sans">
                        {processor.purpose}
                      </TableCell>
                      <TableCell className="py-4 pr-4 sm:pr-6 align-top whitespace-normal min-w-[200px]">
                        <span className="text-xs font-mono text-muted-foreground bg-muted/70 px-2 py-1 rounded-sm block leading-relaxed">
                          {processor.safeguards}
                        </span>
                      </TableCell>
                    </TableRow>)}
                </TableBody>
                <TableCaption className="text-xs text-muted-foreground font-sans px-4 pb-2">
                  All sub-processors are bound by confidentiality clauses and strict prohibition on independent data commercialization.
                </TableCaption>
              </Table>
            </Card>
          </motion.div>
        </motion.div>

        {/* Informational Callout & Exercise Rights Card */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-40px"
      }} variants={itemVariants}>
          <Card className="rounded-md border border-border bg-card p-6 sm:p-8 relative overflow-hidden shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              <div className="lg:col-span-8">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-accent" />
                  <span className="text-xs font-semibold tracking-wider uppercase font-sans text-muted-foreground">
                    Data Subject Rights & Erasure Requests
                  </span>
                </div>
                <h4 className="text-xl sm:text-2xl font-serif font-semibold text-foreground mb-2">
                  Request expedited records deletion or audit copy
                </h4>
                <p className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
                  Under GDPR, CCPA, and global statutory provisions, you may request a machine-readable export or permanent erasure of your personal records at any time. Verifications are processed by our compliance team within 30 calendar days.
                </p>
              </div>

              <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-end items-stretch sm:items-center lg:items-end">
                <Button asChild className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 min-h-[44px] text-sm font-medium w-full sm:w-auto">
                  <a href="mailto:info@minfomedia.com?subject=Data%20Retention%20and%20Erasure%20Request">
                    <Mail className="w-4 h-4 mr-2" />
                    Submit Erasure Request
                  </a>
                </Button>

                <Button asChild variant="outline" className="rounded-sm border-border bg-background hover:bg-muted text-foreground px-5 py-3 min-h-[44px] text-sm font-medium w-full sm:w-auto">
                  <Link to="/TermsConditionsPage">
                    Review Terms & Conditions
                    <ExternalLink className="w-3.5 h-3.5 ml-1.5 opacity-70" />
                  </Link>
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>

      </div>
    </section>;
}